/**
 * custom-volume-pricing.js
 * CCC "Buy more, save more" table — keeps the per-bag tier prices in sync with
 * the selected variant.
 *
 * Why this needs JS at all: coffee products carry one variant per size, and the
 * sizes are nowhere near each other in price (Bolivia Carmelita is $36 for 320g
 * and $109 for 1kg on the same product). A single static "from $X" tier price
 * would be wrong for every size but the cheapest, sitting directly under a
 * price block that says otherwise.
 *
 * It still cannot flash: every tier price for every variant is formatted
 * server-side in Liquid (shop money format, shop rounding) and shipped in the
 * JSON payload below. The first paint is already correct for
 * selected_or_first_available_variant — including ?variant= deep links — and
 * this module only swaps text on variant:update. No client-side currency
 * formatting, no fetch, no layout shift.
 */

import { ThemeEvents } from '@theme/events';

class CccVolumePricing extends HTMLElement {
  connectedCallback() {
    this._prices = this._parsePrices();
    this._cells = Array.from(this.querySelectorAll('[data-ccc-vp-price]'));

    this._boundVariantHandler = (event) => {
      if (!this._isSameProductContext(event)) return;

      const eventProductId = event.detail?.data?.productId;
      const thisProductId = this.dataset.productId;
      if (eventProductId && thisProductId && String(eventProductId) !== String(thisProductId)) {
        return;
      }

      const variantId = event.detail?.resource?.id;
      if (!variantId) return;

      this._render(variantId);
    };

    document.addEventListener(ThemeEvents.variantUpdate, this._boundVariantHandler);
  }

  disconnectedCallback() {
    if (this._boundVariantHandler) {
      document.removeEventListener(ThemeEvents.variantUpdate, this._boundVariantHandler);
    }
  }

  _parsePrices() {
    try {
      const scriptEl = this.querySelector('[data-ccc-vp-variants]');
      return scriptEl ? JSON.parse(scriptEl.textContent) : {};
    } catch {
      return {};
    }
  }

  /**
   * Unknown variant leaves the server-rendered prices in place rather than
   * blanking the column — a stale-but-plausible price beats an empty cell.
   * @param {string|number} variantId
   */
  _render(variantId) {
    const tiers = this._prices[String(variantId)];
    if (!Array.isArray(tiers)) return;

    this._cells.forEach((cell, index) => {
      if (tiers[index]) cell.textContent = tiers[index];
    });
  }

  /** Ignore variant events fired by a different product's section (recommendations, quick add). */
  _isSameProductContext(event) {
    if (!(event.target instanceof HTMLElement)) return true;

    const sourceSection = event.target.closest('.shopify-section, dialog');
    const hostSection = this.closest('.shopify-section, dialog');

    return !sourceSection || !hostSection || sourceSection === hostSection;
  }
}

if (!customElements.get('ccc-volume-pricing')) {
  customElements.define('ccc-volume-pricing', CccVolumePricing);
}
